import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GENERIC_ERROR_TITLE, RETRY } from "@/lib/uiMessages";

interface ErrorScreenProps {
  /** Krótki nagłówek, np. "Błąd ładowania danych" */
  title?: string;
  /** Dłuższy opis tego, co się stało */
  description?: string;
  /** Treść techniczna błędu (pokazujemy w details, składanym) */
  details?: string;
  /** Czy pokazać przycisk „Spróbuj ponownie" (window.location.reload) */
  showReload?: boolean;
  /** Niestandardowa funkcja retry — jeśli podana, zastępuje reload */
  onRetry?: () => void;
  /** Czy zajmować całą wysokość ekranu (true) czy tylko sekcję (false) */
  fullScreen?: boolean;
}

/**
 * Czytelny ekran awarii pokazywany, gdy aplikacja nie może załadować danych
 * lub gdy mapa nie zainicjalizowała się poprawnie.
 *
 * - Stałe komunikaty po polsku (zgodnie z polityką UI).
 * - Przycisk „Spróbuj ponownie" (twardy reload lub custom callback).
 * - Sekcja „Szczegóły techniczne" (collapsible) — przydatna do zgłaszania bugów.
 * - Link mailto do kontaktu.
 */
export const ErrorScreen = ({
  title = GENERIC_ERROR_TITLE,
  description = "Nie udało się załadować danych. Sprawdź połączenie z internetem i spróbuj ponownie.",
  details,
  showReload = true,
  onRetry,
  fullScreen = true,
}: ErrorScreenProps) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      className={
        fullScreen
          ? "min-h-screen bg-background flex items-center justify-center p-4"
          : "w-full h-full flex items-center justify-center p-4"
      }
    >
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle
            className="h-7 w-7 text-destructive"
            aria-hidden="true"
          />
        </div>
        <h1 className="text-xl font-semibold text-foreground mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {description}
        </p>

        {showReload && (
          <Button onClick={handleRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {RETRY}
          </Button>
        )}

        <p className="mt-6 text-xs text-muted-foreground">
          Problem nie ustępuje?{" "}
          <a
            href="mailto:kontakt@gdzieschronisko.pl"
            className="underline hover:text-foreground"
          >
            Napisz do nas
          </a>
          .
        </p>

        {details && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              Szczegóły techniczne
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-muted p-3 text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
              {details}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};
